/**
 * ConversationSearchDrawer — a reusable, full-height side drawer for finding
 * conversations with a rich set of filters (mirrors the KC2 "Search Interaction"
 * screen, rebuilt clean). Results are multi-selectable; the caller decides what
 * happens on confirm (link as child, merge, associate, …), so this component can
 * be reused anywhere a conversation picker is needed.
 *
 * Filters map to GET /conversations query params (see query-filters.ts).
 */
import { useEffect, useState, type JSX } from 'react'
import { X, Search, Check, Loader2, ChevronDown, UserCog, Calendar, Hash, type LucideIcon } from 'lucide-react'
import { api } from '../../api/client.js'
import { R } from './detail/primitives.js'
import ConversationFilterPanel, { type ConversationFilterState, EMPTY_FILTER_STATE } from './ConversationFilterPanel.js'
import {
  DrawerInfoButton,
  DrawerInfoPanel,
  useDrawerInfo,
  type DrawerInfoContent,
} from '../ui/DrawerInfo.js'
import { formatInTimezone } from '../../utils/timezone.js'

export interface ConversationSearchDrawerProps {
  open: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  /** What this search-and-select flow does, behind an ⓘ button in the header.
   *  Omit and no button renders — see ui/DrawerInfo.tsx. */
  info?: DrawerInfoContent
  /** Conversation ids to hide from results (e.g. the parent itself). */
  excludeIds?: string[]
  /** Fixed GET /conversations params for every search — e.g. the child picker's
   *  `excludeClosed` / `withoutParent`. Applied server-side so counts and paging stay right. */
  baseParams?: Record<string, string>
  /** Allow selecting multiple results (default true). */
  multiple?: boolean
  confirmLabel?: string
  /**
   * Called with the selected conversation id(s), plus the loaded rows for
   * those ids — callers that need to show details of the selection (e.g. the
   * merge dialog listing reference ids and requesters) would otherwise have to
   * refetch what this drawer already has. Return true to close.
   */
  onConfirm: (ids: string[], rows?: ConvItem[]) => Promise<boolean> | boolean
}

export interface ConvItem {
  id: string
  referenceId: string
  subject: string | null
  status: string | null
  displayName: string | null
  primaryHandle: string | null
  priorityLabel: string | null
  channelOrigin: string | null
  openedAt: string | null
  lastMessagePreview: string | null
  assignedAgentLoginId: string | null
  categoryId: string | null
  subcategoryId: string | null
  typeId: string | null
  urgencyId: string | null
  impactId: string | null
  slaState: string | null
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return formatInTimezone(d, { day: '2-digit', month: 'short', year: 'numeric' })
}

const PAGE_SIZE = 20

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'chat', label: 'Chat' },
  { value: 'voice', label: 'Voice' },
  { value: 'web', label: 'Web' },
]

const STATUS_PILL: Record<string, string> = {
  open: 'bg-emerald-50 text-emerald-700',
  reopen: 'bg-blue-50 text-blue-700',
  on_hold: 'bg-amber-50 text-amber-700',
  closed: 'bg-slate-100 text-slate-600',
}

export default function ConversationSearchDrawer({
  open,
  onClose,
  title = 'Search Conversations',
  subtitle = 'Filter and select a conversation',
  info,
  excludeIds = [],
  baseParams,
  multiple = true,
  confirmLabel = 'Select',
  onConfirm,
}: ConversationSearchDrawerProps): JSX.Element | null {
  // ── Filter state (managed by ConversationFilterPanel) ──
  const [filterState, setFilterState] = useState<ConversationFilterState>(EMPTY_FILTER_STATE)

  // ── Result state ──
  const [items, setItems] = useState<ConvItem[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [nextOffset, setNextOffset] = useState<number | null>(null)
  const [confirming, setConfirming] = useState(false)
  const drawerInfo = useDrawerInfo(open)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const handleFilterChange = (change: Partial<ConversationFilterState>): void => {
    setFilterState((prev) => ({ ...prev, ...change }))
  }

  const reset = (): void => {
    setFilterState(EMPTY_FILTER_STATE)
    setItems([]); setSelected(new Set()); setSearched(false); setTotalCount(0); setNextOffset(null); setExpanded(new Set())
  }

  // Fresh state each time the drawer opens.
  useEffect(() => {
    if (open) reset()
  }, [open])

  const buildParams = (offset: number): string => {
    const p = new URLSearchParams(baseParams)
    if (filterState.keyword.trim()) p.set('search', filterState.keyword.trim())
    if (filterState.referenceId.trim()) p.set('referenceId', filterState.referenceId.trim())
    if (filterState.status) p.set('status', filterState.status)
    if (filterState.conversationStatusId) p.set('conversationStatusId', filterState.conversationStatusId)
    if (filterState.channel) p.set('channel', filterState.channel)
    if (filterState.typeId) p.set('typeId', filterState.typeId)
    if (filterState.categoryId) p.set('categoryId', filterState.categoryId)
    if (filterState.subcategoryId) p.set('subcategoryId', filterState.subcategoryId)
    if (filterState.priorityId) p.set('priorityId', filterState.priorityId)
    if (filterState.impactId) p.set('impactId', filterState.impactId)
    if (filterState.urgencyId) p.set('urgencyId', filterState.urgencyId)
    if (filterState.complexityId) p.set('complexityId', filterState.complexityId)
    if (filterState.campaignId) p.set('campaignId', filterState.campaignId)
    if (filterState.teamId) p.set('assignedTeamId', filterState.teamId)
    if (filterState.agentIds.length) p.set('assignedAgentIds', filterState.agentIds.join(','))
    if (filterState.locationId) p.set('locationId', filterState.locationId)
    if (filterState.branchId) p.set('branchId', filterState.branchId)
    if (filterState.departmentId) p.set('departmentId', filterState.departmentId)
    if (filterState.subject.trim()) p.set('subject', filterState.subject.trim())
    if (filterState.description.trim()) p.set('description', filterState.description.trim())
    if (filterState.stateId) p.set('stateId', filterState.stateId)
    if (filterState.approvalStateId) p.set('approvalStateId', filterState.approvalStateId)
    if (filterState.approvalLevelId) p.set('approvalLevelId', filterState.approvalLevelId)
    if (filterState.dynamicProperty) p.set('dynamicProperty', filterState.dynamicProperty)
    if (filterState.dynamicPropertyValue) p.set('dynamicPropertyValue', filterState.dynamicPropertyValue)
    if (filterState.from) p.set('from', filterState.from)
    if (filterState.to) p.set('to', filterState.to)
    p.set('limit', String(PAGE_SIZE))
    p.set('offset', String(offset))
    return p.toString()
  }

  const runSearch = async (offset = 0, append = false): Promise<void> => {
    setLoading(true)
    setSearched(true)
    try {
      const res = await api.get<{ items: ConvItem[]; nextOffset: number | null; totalCount: number }>(
        `/conversations?${buildParams(offset)}`,
      )
      const excl = new Set(excludeIds)
      const fresh = (res.items ?? []).filter((c) => !excl.has(c.id))
      setItems((prev) => (append ? [...prev, ...fresh] : fresh))
      setTotalCount(res.totalCount ?? fresh.length)
      setNextOffset(res.nextOffset ?? null)
    } catch {
      if (!append) { setItems([]); setTotalCount(0) }
      setNextOffset(null)
    } finally {
      setLoading(false)
    }
  }

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(multiple ? prev : [])
      if (prev.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleExpand = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const channelLabel = (ch: string | null): string | null =>
    ch ? CHANNEL_OPTIONS.find((o) => o.value === ch)?.label ?? ch : null

  const confirm = async (): Promise<void> => {
    if (selected.size === 0) return
    setConfirming(true)
    try {
      const ok = await onConfirm([...selected], items.filter((i) => selected.has(i.id)))
      if (ok) onClose()
    } finally {
      setConfirming(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="animate-slide-in-right fixed right-0 top-0 z-[61] flex h-full w-[93%] max-w-[1360px] flex-col bg-[#F8FAFC] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-7 py-4">
          <div className="flex items-center gap-3.5">
            <div className={`flex h-11 w-11 items-center justify-center ${R} bg-[#3366FF] text-white shadow-sm`}>
              <Search className="h-[21px] w-[21px]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#0f172a]">{title}</h2>
              <p className="text-xs text-slate-400">{subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {info && <DrawerInfoButton shown={drawerInfo.shown} onToggle={drawerInfo.toggle} />}
            <button
              onClick={onClose}
              aria-label="Close"
              className={`flex h-9 w-9 items-center justify-center ${R} text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {info && (
          <DrawerInfoPanel info={info} shown={drawerInfo.shown} onDismiss={drawerInfo.toggle} />
        )}

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-7 py-5">
          {/* Filters card */}
          <ConversationFilterPanel
            state={filterState}
            onChange={handleFilterChange}
            onReset={reset}
            onApply={() => void runSearch(0)}
            applying={loading}
            applyLabel="Search"
          />

          {/* Results */}
          <div className="mt-5">
            <div className="mb-2 flex items-center justify-between px-1">
              <h3 className="text-sm font-bold text-slate-800">
                Results {searched && !loading ? <span className="text-slate-400">· {totalCount}</span> : null}
              </h3>
              {selected.size > 0 && <span className="text-xs font-medium text-[#3366FF]">{selected.size} selected</span>}
            </div>

            {!searched ? (
              <EmptyState text="Set your filters and hit Search to find conversations." />
            ) : loading && items.length === 0 ? (
              <EmptyState text="Searching…" spinner />
            ) : items.length === 0 ? (
              <EmptyState text="No conversations match these filters." />
            ) : (
              <div className="space-y-2">
                {items.map((c) => {
                  const active = selected.has(c.id)
                  const isExp = expanded.has(c.id)
                  return (
                    <div
                      key={c.id}
                      className={`overflow-hidden ${R} border transition-all ${active ? 'border-[#3366FF] bg-[#F5F7FF] ring-1 ring-[#3366FF]' : 'border-slate-200 bg-white hover:border-[#DCE7F8]'}`}
                    >
                      <div className="flex items-start gap-3 px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggle(c.id)}
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] border transition-colors ${active ? 'border-[#3366FF] bg-[#3366FF] text-white' : 'border-slate-300 bg-white hover:border-[#3366FF]'}`}
                        >
                          {active && <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button type="button" onClick={() => toggle(c.id)} className="min-w-0 flex-1 text-left">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-slate-800">#{c.referenceId}</span>
                            {c.status && (
                              <span className={`rounded-[3px] px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_PILL[c.status] ?? 'bg-slate-100 text-slate-600'}`}>
                                {c.status.replace('_', ' ')}
                              </span>
                            )}
                            {c.priorityLabel && (
                              <span className="rounded-[3px] bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-[#3366FF]">{c.priorityLabel}</span>
                            )}
                            {channelLabel(c.channelOrigin) && (
                              <span className="rounded-[3px] bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{channelLabel(c.channelOrigin)}</span>
                            )}
                          </div>
                          <div className="mt-1 truncate text-sm font-medium text-slate-800">{c.subject || c.lastMessagePreview || '(No subject)'}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-400">
                            <span className="truncate text-slate-500">{c.displayName || c.primaryHandle || 'Unknown'}</span>
                            {c.assignedAgentLoginId && <span className="inline-flex items-center gap-1"><UserCog className="h-3 w-3" />{c.assignedAgentLoginId}</span>}
                            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(c.openedAt)}</span>
                          </div>
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleExpand(c.id)}
                        className="flex w-full items-center justify-center gap-1 border-t border-slate-100 py-1.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-slate-50 hover:text-[#3366FF]"
                      >
                        {isExp ? 'See less' : 'See more'}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExp ? 'rotate-180' : ''}`} />
                      </button>

                      {isExp && (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-slate-100 bg-slate-50/60 px-4 py-3 sm:grid-cols-3">
                          <Detail icon={Hash} label="Reference" value={c.referenceId} />
                          <Detail icon={Calendar} label="Created" value={formatDate(c.openedAt)} />
                          <Detail label="Channel" value={channelLabel(c.channelOrigin)} />
                          <Detail icon={UserCog} label="Agent" value={c.assignedAgentLoginId} />
                          <Detail label="Priority" value={c.priorityLabel} />
                          <Detail label="SLA" value={c.slaState} />
                        </div>
                      )}
                    </div>
                  )
                })}

                {nextOffset !== null && (
                  <button
                    type="button"
                    onClick={() => void runSearch(nextOffset, true)}
                    disabled={loading}
                    className={`w-full ${R} border border-dashed border-slate-300 bg-white py-2.5 text-sm font-semibold text-[#3366FF] transition-colors hover:bg-[#F5F7FF] disabled:opacity-60`}
                  >
                    {loading ? 'Loading…' : `Load more (${items.length} of ${totalCount})`}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-7 py-4">
          <span className="text-xs text-slate-400">
            {selected.size > 0 ? `${selected.size} conversation${selected.size > 1 ? 's' : ''} selected` : 'Select one or more results'}
          </span>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className={`${R} px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100`}>Cancel</button>
            <button
              onClick={() => void confirm()}
              disabled={selected.size === 0 || confirming}
              className={`inline-flex items-center gap-2 ${R} bg-[#3366FF] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

function EmptyState({ text, spinner }: { text: string; spinner?: boolean }): JSX.Element {
  return (
    <div className={`flex flex-col items-center justify-center gap-2 ${R} border border-dashed border-slate-200 bg-white py-12 text-center`}>
      {spinner ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Search className="h-6 w-6 text-slate-300" />}
      <p className="text-sm text-slate-400">{text}</p>
    </div>
  )
}

function Detail({ icon: Icon, label, value }: { icon?: LucideIcon; label: string; value: string | null }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-0.5 truncate text-xs font-medium text-slate-700" title={value ?? undefined}>{value || '—'}</div>
    </div>
  )
}
